import React from 'react';
import styled from 'styled-components';
import { Colors, Fonts } from '../utils/colors';

export const PromptField = (props: {
  label?: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
  multiline?: boolean
  rows?: number
  type?: string
  disabled?: boolean
  error?: boolean
  helperText?: string
  autoFocus?: boolean
  onKeyPress?: (event: React.KeyboardEvent) => void
}) => {
  return (
    <Wrap>
      {props.label && <Label>{props.label}</Label>}
      <Row $error={!!props.error}>
        <Caret>&gt;</Caret>
        {props.multiline ? (
          <Area
            rows={props.rows || 3}
            value={props.value}
            onChange={props.onChange}
            placeholder={props.placeholder}
            disabled={props.disabled}
            autoFocus={props.autoFocus}
            onKeyPress={props.onKeyPress}
            spellCheck={false}
          />
        ) : (
          <Input
            type={props.type || "text"}
            value={props.value}
            onChange={props.onChange}
            placeholder={props.placeholder}
            disabled={props.disabled}
            autoFocus={props.autoFocus}
            onKeyPress={props.onKeyPress}
            spellCheck={false}
          />
        )}
      </Row>
      {props.helperText != null && (
        <Hint $error={!!props.error}>{props.helperText}</Hint>
      )}
    </Wrap>
  )
}

const Wrap = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${Fonts.mono};
`;

const Label = styled.span`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${Colors.terminalMuted};
`;

const Row = styled.div<{$error?: boolean}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${props => props.$error ? Colors.destructRed : Colors.terminalBorder};
  background: ${Colors.fieldBg};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${props => props.$error ? Colors.destructRed : Colors.destructGreen};
    box-shadow: 0 0 0 1px ${props => props.$error ? Colors.destructRed : Colors.accentFocus};
  }
`;

const Caret = styled.span`
  flex-shrink: 0;
  margin-top: 1px;
  color: ${Colors.destructGreen};
  text-shadow: 0 0 8px ${Colors.terminalGlow};
  user-select: none;
`;

const fieldReset = `
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: ${Colors.terminalText};
  font-family: ${Fonts.mono};
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  caret-color: ${Colors.destructGreen};

  &::placeholder {
    color: ${Colors.terminalDim};
  }

  &:disabled {
    opacity: 0.55;
  }
`;

const Input = styled.input`
  ${fieldReset}
`;

const Area = styled.textarea`
  ${fieldReset}
`;

const Hint = styled.span<{$error?: boolean}>`
  min-height: 16px;
  font-size: 12px;
  color: ${props => props.$error ? Colors.destructRed : Colors.terminalMuted};
`;
